import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Grid,
  Pagination,
  Stack,
  CircularProgress,
  Select,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SchoolIcon from "@mui/icons-material/School";
import EventIcon from "@mui/icons-material/Event";
import PeopleIcon from "@mui/icons-material/People";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import LoginIcon from "@mui/icons-material/Login";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTenant } from "../contexts/TenantContext";
import PublicCenterInfo from "../components/PublicCenterInfo";
import {
  fetchPublicClasses,
  fetchClasses,
  fetchAllTenants,
  fetchSystemHealth,
  fetchConfigList,
} from "../api/apiClient";

// ── Design tokens (matching reference design) ─────────────────────────────────
// primary/secondary/accent come from the MUI theme (tenant-configurable).
const DS = {
  surface: "#ffffff",
  surfaceContainer: "var(--color-surface-press-light)",
  surfaceContainerLow: "var(--color-surface-2)",
  surfaceContainerLowest: "#ffffff",
  onSurface: "var(--color-ink)",
  onSurfaceVariant: "var(--color-ink-2)",
  outline: "var(--color-ink-3)",
  outlineVariant: "var(--color-hairline-cool)",
  error: "var(--color-danger)",
};

const CATEGORY_COLORS = {
  Firefighter: "#c62828",
  EMS: "#1565c0",
  Police: "#283593",
  Rescue: "#e65100",
  Lifeguard: "#0277bd",
  Security: "#4a148c",
  ADMIN: "#37474f",
  AHA: "#880e4f",
  TACTICAL: "#1b5e20",
  Other: "#5d4037",
};

const CATEGORY_BADGE = {
  Firefighter: { bg: "rgba(198,40,40,0.1)", color: "#c62828" },
  EMS: { bg: "rgba(21,101,192,0.1)", color: "#1565c0" },
  Police: { bg: "rgba(40,53,147,0.1)", color: "#283593" },
  Rescue: { bg: "rgba(230,81,0,0.1)", color: "#e65100" },
  Lifeguard: { bg: "rgba(2,119,189,0.1)", color: "#0277bd" },
  Security: { bg: "rgba(74,20,140,0.1)", color: "#4a148c" },
  ADMIN: { bg: "rgba(55,71,79,0.1)", color: "#37474f" },
  AHA: { bg: "rgba(136,14,79,0.1)", color: "#880e4f" },
  TACTICAL: { bg: "rgba(27,94,32,0.1)", color: "#1b5e20" },
  Other: { bg: "rgba(93,64,55,0.1)", color: "#5d4037" },
};

const DEFAULT_CATEGORIES = [
  "Firefighter","EMS","Police","Rescue","Lifeguard",
  "Security","ADMIN","AHA","TACTICAL","Other",
];

const IS_MOCK = process.env.REACT_APP_LOGIN_MODE === "mock";

const PER_PAGE = 12;

// ── Legal / policy modal content ───────────────────────────────────────────────
const LEGAL_CONTENT = {
  privacy: {
    title: "Privacy Policy",
    sections: [
      {
        heading: "Information We Collect",
        body: "When you create an account, enroll in a course, or contact us for support, we collect information such as your name, email address, phone number, employer/agency, and professional certification details. We also automatically collect limited technical information (browser type, IP address, and pages visited) to keep the site secure and functioning correctly.",
      },
      {
        heading: "How We Use Your Information",
        body: "We use your information to register you for training courses, track certification and continuing-education progress, communicate class schedules and updates, process enrollments, and improve the Training Center platform. We do not sell your personal information.",
      },
      {
        heading: "Data Sharing",
        body: "Your information may be shared with your sponsoring agency or organization for the purpose of verifying training completion and certification status. We may also share data with service providers who help us operate the platform (e.g., hosting, email delivery), under agreements that limit their use of your data.",
      },
      {
        heading: "Data Security",
        body: "We use industry-standard safeguards, including encryption of sensitive personal data at rest and in transit, access controls, and tenant-level data isolation, to protect your information from unauthorized access, alteration, or disclosure.",
      },
      {
        heading: "Your Rights",
        body: "You may request access to, correction of, or deletion of your personal information at any time by contacting your training center administrator or our support team. Some records may be retained where required for regulatory or certification purposes.",
      },
      {
        heading: "Cookies",
        body: "We use essential cookies to keep you signed in and remember your preferences. We do not use third-party advertising cookies.",
      },
      {
        heading: "Children's Privacy",
        body: "The Training Center platform is intended for professional and vocational training use and is not directed at children under 13.",
      },
      {
        heading: "Changes to This Policy",
        body: "We may update this Privacy Policy from time to time. Material changes will be posted on this page with an updated effective date.",
      },
      {
        heading: "Contact Us",
        body: "Questions about this policy can be directed to your training center's administrative office or to support@trainingcenter.example.",
      },
    ],
  },
  terms: {
    title: "Terms of Service",
    sections: [
      {
        heading: "Acceptance of Terms",
        body: "By accessing or using the Training Center platform, you agree to be bound by these Terms of Service. If you do not agree, please do not use the platform.",
      },
      {
        heading: "Account Registration",
        body: "You must provide accurate, current information when creating an account and are responsible for maintaining the confidentiality of your login credentials and for all activity under your account.",
      },
      {
        heading: "Course Enrollment & Cancellation",
        body: "Enrollment in a course is subject to seat availability and confirmation by the training center. Cancellation, rescheduling, and waitlist policies may vary by course and will be communicated at the time of registration.",
      },
      {
        heading: "User Conduct",
        body: "You agree to use the platform only for lawful purposes and not to misrepresent your identity, credentials, or certification status, or to interfere with the operation of the platform or other users' access to it.",
      },
      {
        heading: "Intellectual Property",
        body: "Course materials, curricula, and platform content are the property of the applicable training center or its licensors and may not be reproduced or distributed without permission.",
      },
      {
        heading: "Disclaimers",
        body: "Training courses are provided for professional development purposes. Completion of a course does not guarantee certification, employment, or licensure, which remain subject to the requirements of the relevant certifying or regulatory body.",
      },
      {
        heading: "Limitation of Liability",
        body: "To the fullest extent permitted by law, the Training Center platform and its operators are not liable for indirect, incidental, or consequential damages arising from use of the platform or attendance at a course.",
      },
      {
        heading: "Termination",
        body: "We reserve the right to suspend or terminate access to the platform for violations of these Terms or for misuse of the system.",
      },
      {
        heading: "Governing Law",
        body: "These Terms are governed by the laws of the jurisdiction in which the operating training center is located, without regard to conflict-of-law principles.",
      },
      {
        heading: "Changes to These Terms",
        body: "We may revise these Terms periodically. Continued use of the platform after changes are posted constitutes acceptance of the revised Terms.",
      },
      {
        heading: "Contact Us",
        body: "Questions about these Terms can be directed to your training center's administrative office or to support@trainingcenter.example.",
      },
    ],
  },
  accessibility: {
    title: "Accessibility",
    sections: [
      {
        heading: "Our Commitment",
        body: "Training Center is committed to ensuring digital accessibility for people of all abilities, including current and prospective students, instructors, and administrators. We are actively working to improve the usability and accessibility of our platform for everyone.",
      },
      {
        heading: "Conformance Standard",
        body: "We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1, Level AA, which help make web content more accessible to people with a wide range of disabilities.",
      },
      {
        heading: "Measures We Take",
        body: "Our platform is built with semantic HTML, keyboard-navigable controls, sufficient color contrast, and descriptive labeling for screen readers. We periodically review new features against these standards before release.",
      },
      {
        heading: "Compatibility",
        body: "The platform is designed to work with recent versions of major browsers and commonly used assistive technologies, such as screen readers and voice-control software.",
      },
      {
        heading: "Known Limitations",
        body: "Despite our efforts, some pages or third-party content may not yet be fully accessible. We are actively working to identify and resolve these issues.",
      },
      {
        heading: "Feedback",
        body: "If you encounter an accessibility barrier while using Training Center, please let us know. We welcome your feedback and will make reasonable efforts to address issues promptly.",
      },
      {
        heading: "Contact Us",
        body: "Accessibility feedback can be sent to your training center's administrative office or to accessibility@trainingcenter.example.",
      },
    ],
  },
};

// ── Contact Us content ──────────────────────────────────────────────────────────
const CONTACT_INFO = {
  email: "support@atomicsolutions.com",
  about:
    "Atomic Solutions builds software that helps public-safety and professional training organizations run their programs from end to end — course scheduling, instructor management, skill and knowledge assessments, certification tracking, and compliance reporting, all in one place.",
  dedication:
    "We believe the people who train first responders, medics, and other frontline professionals deserve tools that are as reliable as the work they do. Every feature we ship is built with one goal: to make it easier for instructors to teach, for students to learn, and for agencies to prove their people are ready. Continuous learning isn't a checkbox for us — it's the reason Atomic Solutions exists.",
};

// ── Knowledge Base content ──────────────────────────────────────────────────────
const KB_TOPICS = [
  {
    id: "browse-enroll",
    title: "Browsing & Enrolling in Courses",
    body: "The home page lists every upcoming course open for registration. Use the search bar to find a course by name or course code, or use the category pills (Firefighter, EMS, Police, Rescue, etc.) to narrow the list. Click any course card to see full details, prerequisites, and seat availability. Enrolling requires a free account — click \"Enroll Now\" and you'll be prompted to sign in first.",
  },
  {
    id: "account-signin",
    title: "Creating an Account & Signing In",
    body: "New students are typically invited by their agency or training center administrator via email. If you already have an account, click \"Sign In\" in the top navigation. Forgot your password? Use the \"Forgot Password\" link on the sign-in page to receive a reset email.",
  },
  {
    id: "student-hub",
    title: "Student Course Hub",
    body: "Once enrolled, each course has its own Student Hub with tabs for Documents (course materials), Skill Sheets (hands-on skills evaluations), Tests (knowledge assessments), Roster (your classmates), and Assignments. This is the central place to track your progress through a course.",
  },
  {
    id: "certifications-transcript",
    title: "Certifications & Transcript",
    body: "The Certifications page lists every credential you've earned, including issue and expiration dates. Your Transcript page compiles your full training history into a single downloadable record you can share with an employer or certifying body.",
  },
  {
    id: "instructor-course-hub",
    title: "Instructor Course Hub",
    body: "Instructors manage a course from its Course Hub: taking attendance session-by-session, assessing student skill checklists, grading tests and scenarios, and generating course completion reports. Instructor tools also include a Knowledge Bank of reusable skill sheets, tests, and documents to speed up building new courses.",
  },
  {
    id: "course-management",
    title: "Course Management & Approvals",
    body: "Course coordinators and admins can submit new course proposals, schedule sessions from existing templates, and route new courses through an approval workflow before they go live. Approved courses then become available for instructors to run and students to enroll in.",
  },
  {
    id: "agency-management",
    title: "Agency Management",
    body: "Agency administrators can view member compliance status, manage which of their members are enrolled in which classes, set role-based training requirements, and (where applicable) track Certificates of Insurance (COI) for their agency.",
  },
  {
    id: "scheduling",
    title: "Scheduling, Semesters, Rooms & Equipment",
    body: "Training centers plan coursework by semester, scheduling classes into rooms and reserving equipment as needed. Semester Planning gives coordinators a bird's-eye view of everything scheduled across a term.",
  },
  {
    id: "messaging-profile",
    title: "Messaging & Profile Settings",
    body: "Use the built-in Messaging page to communicate with instructors, students, or staff without leaving the platform. Your Profile page is where you update contact information, change your password, and review your account details.",
  },
];

const KB_FAQS = [
  {
    q: "How do I register for a class?",
    a: "Find the class on the home page or course catalog, click on it to open the details, then click \"Enroll Now\" (or \"Register for Class\" in the details popup). You'll need to sign in first if you aren't already.",
  },
  {
    q: "I forgot my password. What do I do?",
    a: "On the sign-in page, click \"Forgot Password\" and enter your email address. You'll receive a link to reset your password.",
  },
  {
    q: "How do I get my certificate or transcript?",
    a: "Sign in and go to the Certifications page to view individual credentials, or the Transcript page to download your complete training record.",
  },
  {
    q: "A class I want is full — what are my options?",
    a: "Full classes show a \"Join Waitlist\" option instead of \"Enroll Now.\" Joining the waitlist notifies you if a seat opens up.",
  },
  {
    q: "How do I know if I've completed a course?",
    a: "Course completion status is shown in your Student Hub for that course, and completed courses appear on your Transcript once all requirements (tests, skill sheets, attendance) are signed off by an instructor.",
  },
  {
    q: "Can I use the site on my phone or tablet?",
    a: "Yes, the site is fully responsive and works on modern mobile browsers as well as desktop.",
  },
  {
    q: "How is my personal information protected?",
    a: "We encrypt sensitive personal data and isolate each training center's data from others. See the Privacy Policy for full details.",
  },
  {
    q: "Who do I contact for help that isn't covered here?",
    a: "Use the Contact Us link in the footer to reach us directly, or contact your training center's administrator for account-specific issues.",
  },
];

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

// ── Course Card ───────────────────────────────────────────────────────────────
function CourseCard({ cls, onSelect }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDark = theme.palette.primary.dark;
  const tSecondary = theme.palette.secondary.main;
  const seatsLeft = cls.availableSeats;
  const isFull = seatsLeft <= 0;
  const accentColor = CATEGORY_COLORS[cls.category] || tPrimary;
  const badge = CATEGORY_BADGE[cls.category] || { bg: alpha(tPrimary, 0.1), color: tPrimary };

  return (
    <Paper
      elevation={0}
      onClick={onSelect}
      sx={{
        borderRadius: "12px",
        border: `1px solid ${DS.outlineVariant}30`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        cursor: "pointer",
        transition: "border-color 0.2s, box-shadow 0.2s",
        bgcolor: DS.surfaceContainerLowest,
        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -2px rgba(0,0,0,0.02)",
        "&:hover": {
          borderColor: alpha(tPrimary, 0.19),
          boxShadow: `0 8px 24px ${alpha(tPrimary, 0.1)}`,
        },
      }}
    >
      {/* Colored accent top bar */}
      <Box sx={{ height: 4, bgcolor: accentColor, width: "100%", flexShrink: 0 }} />

      <Box sx={{ p: 3, display: "flex", flexDirection: "column", flex: 1 }}>
        {/* Header row: category badge + course code */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box sx={{
            px: 1.5, py: 0.5, borderRadius: "4px",
            bgcolor: badge.bg, color: badge.color,
            fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}>
            {cls.category || "Uncategorized"}
          </Box>
          {cls.courseNumber && (
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.06em", color: DS.outline }}>
              {cls.courseNumber}
            </Typography>
          )}
        </Stack>

        {/* Title */}
        <Typography
          variant="body1"
          fontWeight={700}
          color={tPrimary}
          sx={{
            mb: 1, lineHeight: 1.4, fontSize: "1rem",
            transition: "color 0.15s",
            ".MuiPaper-root:hover &": { color: accentColor },
            fontFamily: "var(--font-display)",
          }}
        >
          {cls.title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          color={DS.onSurfaceVariant}
          sx={{
            mb: 3, lineHeight: 1.6, fontSize: "0.875rem",
            display: "-webkit-box", WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical", overflow: "hidden",
            flex: 1,
          }}
        >
          {cls.description || "No description provided."}
        </Typography>

        {/* Date + availability */}
        <Stack spacing={1} sx={{ mb: 3 }}>
          {cls.date && (
            <Stack direction="row" spacing={1} alignItems="center" color={DS.onSurfaceVariant}>
              <EventIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontSize: "0.875rem" }}>
                {formatDate(cls.date)}
              </Typography>
            </Stack>
          )}
          <Stack
            direction="row" spacing={1} alignItems="center"
            color={isFull ? DS.error : tSecondary}
          >
            {isFull ? (
              <PersonOffIcon sx={{ fontSize: 16 }} />
            ) : (
              <PeopleIcon sx={{ fontSize: 16 }} />
            )}
            <Typography variant="caption" fontWeight={700} sx={{ fontSize: "0.875rem" }}>
              {isFull ? "Class full" : `${seatsLeft} seat${seatsLeft !== 1 ? "s" : ""} available`}
            </Typography>
          </Stack>
        </Stack>

        {/* CTA button */}
        {isFull ? (
          <Button
            disabled
            fullWidth
            sx={{
              borderRadius: "8px", border: `1px solid ${DS.outlineVariant}`,
              color: DS.outline, bgcolor: DS.surfaceContainerLow,
              fontWeight: 700, fontSize: "0.7rem", letterSpacing: "0.06em",
              textTransform: "uppercase", py: 1.25, cursor: "not-allowed",
            }}
          >
            Join Waitlist
          </Button>
        ) : (
          <Button
            fullWidth
            sx={{
              borderRadius: "8px", bgcolor: tPrimary, color: "#fff",
              fontWeight: 700, fontSize: "0.7rem", letterSpacing: "0.06em",
              textTransform: "uppercase", py: 1.25,
              "&:hover": { bgcolor: tPrimaryDark },
              transition: "all 0.15s",
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            Enroll Now
          </Button>
        )}
      </Box>
    </Paper>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PublicHomePage() {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDark = theme.palette.primary.dark;
  const tSecondary = theme.palette.secondary.main;
  const tSecondaryLight = theme.palette.secondary.light;
  const navigate = useNavigate();
  const { user, domainTenant } = useTenant();
  const [searchParams] = useSearchParams();
  const queryTenantId = searchParams.get("tenant");

  const [classSource, setClassSource] = useState(
    queryTenantId ? String(queryTenantId) : "public",
  );
  const [allTenants, setAllTenants] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(1);
  const [tenantName, setTenantName] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [legalModal, setLegalModal] = useState(null); // "privacy" | "terms" | "accessibility" | null
  const [supportModal, setSupportModal] = useState(null); // "status" | "contact" | "kb" | null
  const [healthStatus, setHealthStatus] = useState(null); // { up, service, checkedAt } | null
  const [healthLoading, setHealthLoading] = useState(false);
  const [kbSearch, setKbSearch] = useState("");

  const checkSystemHealth = useCallback(() => {
    setHealthLoading(true);
    fetchSystemHealth()
      .then(setHealthStatus)
      .finally(() => setHealthLoading(false));
  }, []);

  useEffect(() => {
    if (supportModal === "status") checkSystemHealth();
  }, [supportModal, checkSystemHealth]);

  const kbQuery = kbSearch.trim().toLowerCase();
  const filteredKbTopics = React.useMemo(() => {
    if (!kbQuery) return KB_TOPICS;
    return KB_TOPICS.filter(
      (t) => t.title.toLowerCase().includes(kbQuery) || t.body.toLowerCase().includes(kbQuery),
    );
  }, [kbQuery]);
  const filteredKbFaqs = React.useMemo(() => {
    if (!kbQuery) return KB_FAQS;
    return KB_FAQS.filter(
      (f) => f.q.toLowerCase().includes(kbQuery) || f.a.toLowerCase().includes(kbQuery),
    );
  }, [kbQuery]);

  const viewMode = classSource === "public" ? "public" : "tenant";
  const selectedTenantForView = classSource === "public" ? null : classSource;

  const [CATEGORIES, setCategories] = useState(DEFAULT_CATEGORIES);

  // Sync state from domain resolution in real mode
  useEffect(() => {
    if (IS_MOCK || !domainTenant) return;
    if (domainTenant.type === "tenant") {
      setClassSource(String(domainTenant.id));
      setTenantName(domainTenant.name);
      fetchConfigList(String(domainTenant.id), "COURSE_CATEGORY")
        .then((items) => setCategories(items.length ? items.map((i) => i.value) : DEFAULT_CATEGORIES))
        .catch(() => setCategories(DEFAULT_CATEGORIES));
    } else if (domainTenant.type === "public") {
      // Public listing domain — populate tenant selector from backend response
      setAllTenants(Array.isArray(domainTenant.tenants) ? domainTenant.tenants : []);
      setClassSource("public");
    }
  }, [domainTenant]);

  useEffect(() => {
    if (!IS_MOCK) return; // real mode: list comes from domain resolution above
    fetchAllTenants()
      .then((tenants) => {
        setAllTenants(Array.isArray(tenants) ? tenants : []);
        if (queryTenantId && tenants) {
          const t = tenants.find((t) => String(t.id) === String(queryTenantId));
          if (t) setTenantName(t.name);
        }
      })
      .catch(() => setAllTenants([]));
  }, [queryTenantId]);

  const loadClasses = useCallback(() => {
    setLoading(true);
    if (viewMode === "public") {
      fetchPublicClasses(selectedTenantForView, null)
        .then((data) => setClasses(Array.isArray(data) ? data : data.data || []))
        .catch(() => setClasses([]))
        .finally(() => setLoading(false));
    } else {
      if (!selectedTenantForView) {
        setClasses([]);
        setLoading(false);
        return;
      }
      fetchClasses(selectedTenantForView, null, false)
        .then((data) => setClasses(Array.isArray(data) ? data : data.data || []))
        .catch(() => setClasses([]))
        .finally(() => setLoading(false));
    }
  }, [viewMode, selectedTenantForView]);

  useEffect(() => { loadClasses(); }, [loadClasses]);

  const filtered = React.useMemo(() => {
    const q = filter.toLowerCase().trim();
    return classes.filter((c) => {
      const matchesSearch = !q || [c.title, c.courseNumber, c.category, c.description]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
      const matchesCategory = !categoryFilter || c.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [classes, filter, categoryFilter]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const handleFilterChange = (val) => { setFilter(val); setPage(1); };
  const handleCategoryChange = (val) => { setCategoryFilter(val); setPage(1); };
  const handleSelectClass = (cls) => { setSelectedClass(cls); setDetailDialogOpen(true); };
  const handleRegister = () => {
    if (!user) {
      navigate("/login", { state: { redirectTo: "/classes/" + selectedClass.id } });
    } else {
      navigate("/classes/" + selectedClass.id);
      setDetailDialogOpen(false);
    }
  };

  const handleSourceChange = (newSource) => {
    setClassSource(newSource);
    setPage(1);
    if (newSource === "public") {
      setTenantName(null);
    } else {
      const t = allTenants.find((t) => String(t.id) === String(newSource));
      if (t) setTenantName(t.name);
    }
  };

  const NAV_HEIGHT = 72;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: DS.surface, display: "flex", flexDirection: "column" }}>

      {/* ── Class Detail Modal ───────────────────────────────────────────────── */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography variant="h6" fontWeight={700} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>{selectedClass?.title}</Typography>
          <Button size="small" onClick={() => setDetailDialogOpen(false)} sx={{ minWidth: "auto", p: 0.5 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </Button>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          {selectedClass && (
            <Stack spacing={2}>
              {selectedClass.courseNumber && (
                <Box>
                  <Typography variant="caption" color={DS.onSurfaceVariant} sx={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Course Number</Typography>
                  <Typography variant="body2" fontWeight={600} color={DS.onSurface}>{selectedClass.courseNumber}</Typography>
                </Box>
              )}
              {selectedClass.category && (
                <Box>
                  <Typography variant="caption" color={DS.onSurfaceVariant} sx={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", display: "block", mb: 0.5 }}>Category</Typography>
                  <Chip label={selectedClass.category} size="small" sx={{
                    bgcolor: CATEGORY_COLORS[selectedClass.category] || tPrimary,
                    color: "#fff", fontWeight: 700,
                  }} />
                </Box>
              )}
              {selectedClass.date && (
                <Box>
                  <Typography variant="caption" color={DS.onSurfaceVariant} sx={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Date</Typography>
                  <Typography variant="body2" fontWeight={600} color={DS.onSurface}>{formatDate(selectedClass.date)}</Typography>
                </Box>
              )}
              {selectedClass.availableSeats !== undefined && (
                <Box>
                  <Typography variant="caption" color={DS.onSurfaceVariant} sx={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Availability</Typography>
                  <Typography variant="body2" fontWeight={600} color={selectedClass.availableSeats > 0 ? tSecondary : DS.error}>
                    {selectedClass.availableSeats > 0 ? `${selectedClass.availableSeats} seats available` : "Class full"}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color={DS.onSurfaceVariant} sx={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Description</Typography>
                <Typography variant="body2" color={DS.onSurface} sx={{ mt: 0.5, lineHeight: 1.6 }}>
                  {selectedClass.description || "No description provided."}
                </Typography>
              </Box>
              {selectedClass.prereqs && selectedClass.prereqs !== "None" && (
                <Box>
                  <Typography variant="caption" color={DS.onSurfaceVariant} sx={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Prerequisites</Typography>
                  <Typography variant="body2" color={DS.onSurface}>{selectedClass.prereqs}</Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDetailDialogOpen(false)} sx={{ textTransform: "none", color: DS.onSurfaceVariant }}>Close</Button>
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={handleRegister}
            sx={{ bgcolor: tPrimary, color: "#fff", textTransform: "none", fontWeight: 700, borderRadius: "8px", boxShadow: "none", "&:hover": { bgcolor: tPrimaryDark, boxShadow: "none" } }}
          >
            {user ? "Register for Class" : "Sign In to Register"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Privacy / Terms / Accessibility Modal ───────────────────────────── */}
      <Dialog open={!!legalModal} onClose={() => setLegalModal(null)} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography variant="h6" fontWeight={700} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>
            {legalModal && LEGAL_CONTENT[legalModal].title}
          </Typography>
          <Button size="small" onClick={() => setLegalModal(null)} sx={{ minWidth: "auto", p: 0.5 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </Button>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          {legalModal && (
            <Stack spacing={2.5}>
              <Typography variant="caption" color={DS.onSurfaceVariant}>
                Effective date: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </Typography>
              {LEGAL_CONTENT[legalModal].sections.map((s) => (
                <Box key={s.heading}>
                  <Typography variant="subtitle2" fontWeight={700} color={DS.onSurface} sx={{ mb: 0.5 }}>
                    {s.heading}
                  </Typography>
                  <Typography variant="body2" color={DS.onSurfaceVariant} sx={{ lineHeight: 1.7 }}>
                    {s.body}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={() => setLegalModal(null)}
            sx={{ bgcolor: tPrimary, color: "#fff", textTransform: "none", fontWeight: 700, borderRadius: "8px", boxShadow: "none", "&:hover": { bgcolor: tPrimaryDark, boxShadow: "none" } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── System Status Modal ─────────────────────────────────────────────── */}
      <Dialog open={supportModal === "status"} onClose={() => setSupportModal(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography variant="h6" fontWeight={700} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>System Status</Typography>
          <Button size="small" onClick={() => setSupportModal(null)} sx={{ minWidth: "auto", p: 0.5 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </Button>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3, pb: 3 }}>
          <Stack spacing={2} alignItems="center" sx={{ textAlign: "center" }}>
            {healthLoading ? (
              <>
                <CircularProgress size={40} sx={{ color: tPrimary }} />
                <Typography variant="body2" color={DS.onSurfaceVariant}>Checking backend status…</Typography>
              </>
            ) : healthStatus ? (
              <>
                {healthStatus.up ? (
                  <CheckCircleIcon sx={{ fontSize: 48, color: tSecondary }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 48, color: DS.error }} />
                )}
                <Typography variant="subtitle1" fontWeight={700} color={healthStatus.up ? tSecondary : DS.error}>
                  {healthStatus.up ? "All Systems Operational" : "Service Unavailable"}
                </Typography>
                <Typography variant="body2" color={DS.onSurfaceVariant}>
                  {healthStatus.up
                    ? "The Training Center backend is responding normally."
                    : "We're unable to reach the Training Center backend right now. Please try again shortly."}
                </Typography>
                <Typography variant="caption" color={DS.outline}>
                  Last checked: {new Date(healthStatus.checkedAt).toLocaleTimeString()}
                </Typography>
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setSupportModal(null)} sx={{ textTransform: "none", color: DS.onSurfaceVariant }}>Close</Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={checkSystemHealth}
            disabled={healthLoading}
            sx={{ bgcolor: tPrimary, color: "#fff", textTransform: "none", fontWeight: 700, borderRadius: "8px", boxShadow: "none", "&:hover": { bgcolor: tPrimaryDark, boxShadow: "none" } }}
          >
            Check Again
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Contact Us Modal ─────────────────────────────────────────────────── */}
      <Dialog open={supportModal === "contact"} onClose={() => setSupportModal(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography variant="h6" fontWeight={700} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>Contact Us</Typography>
          <Button size="small" onClick={() => setSupportModal(null)} sx={{ minWidth: "auto", p: 0.5 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </Button>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2.5}>
            <Paper
              elevation={0}
              sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2, borderRadius: "10px", bgcolor: DS.surfaceContainerLow }}
            >
              <MailOutlineIcon sx={{ color: tPrimary }} />
              <Box>
                <Typography variant="caption" color={DS.onSurfaceVariant} sx={{ display: "block", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, fontSize: "0.65rem" }}>
                  Email us
                </Typography>
                <Typography
                  component="a"
                  href={`mailto:${CONTACT_INFO.email}`}
                  variant="body1"
                  fontWeight={700}
                  color={tPrimary}
                  sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                >
                  {CONTACT_INFO.email}
                </Typography>
              </Box>
            </Paper>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color={DS.onSurface} sx={{ mb: 0.5 }}>
                About Atomic Solutions
              </Typography>
              <Typography variant="body2" color={DS.onSurfaceVariant} sx={{ lineHeight: 1.7 }}>
                {CONTACT_INFO.about}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color={DS.onSurface} sx={{ mb: 0.5 }}>
                Our Dedication to Learning
              </Typography>
              <Typography variant="body2" color={DS.onSurfaceVariant} sx={{ lineHeight: 1.7 }}>
                {CONTACT_INFO.dedication}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={() => setSupportModal(null)}
            sx={{ bgcolor: tPrimary, color: "#fff", textTransform: "none", fontWeight: 700, borderRadius: "8px", boxShadow: "none", "&:hover": { bgcolor: tPrimaryDark, boxShadow: "none" } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Knowledge Base Modal ─────────────────────────────────────────────── */}
      <Dialog
        open={supportModal === "kb"}
        onClose={() => { setSupportModal(null); setKbSearch(""); }}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { height: { xs: "90vh", md: "80vh" } } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography variant="h6" fontWeight={700} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>Knowledge Base</Typography>
          <Button size="small" onClick={() => { setSupportModal(null); setKbSearch(""); }} sx={{ minWidth: "auto", p: 0.5 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </Button>
        </DialogTitle>
        <Divider />
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search help topics and questions…"
            value={kbSearch}
            onChange={(e) => setKbSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: DS.outline, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </Box>
        <DialogContent sx={{ pt: 0 }}>
          <Stack spacing={3}>
            <Box>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: DS.onSurfaceVariant, mb: 1.5 }}>
                Site Basics & Features
              </Typography>
              {filteredKbTopics.length === 0 ? (
                <Typography variant="body2" color={DS.onSurfaceVariant}>No help topics match your search.</Typography>
              ) : (
                <Stack spacing={2}>
                  {filteredKbTopics.map((t) => (
                    <Box key={t.id} sx={{ pb: 1.5, borderBottom: `1px solid ${DS.outlineVariant}40` }}>
                      <Typography variant="subtitle2" fontWeight={700} color={DS.onSurface} sx={{ mb: 0.5 }}>
                        {t.title}
                      </Typography>
                      <Typography variant="body2" color={DS.onSurfaceVariant} sx={{ lineHeight: 1.7 }}>
                        {t.body}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: DS.onSurfaceVariant, mb: 1.5 }}>
                Common Questions
              </Typography>
              {filteredKbFaqs.length === 0 ? (
                <Typography variant="body2" color={DS.onSurfaceVariant}>No questions match your search.</Typography>
              ) : (
                <Stack spacing={2}>
                  {filteredKbFaqs.map((f) => (
                    <Box key={f.q}>
                      <Typography variant="body2" fontWeight={700} color={tPrimary} sx={{ mb: 0.5 }}>
                        Q: {f.q}
                      </Typography>
                      <Typography variant="body2" color={DS.onSurfaceVariant} sx={{ lineHeight: 1.7 }}>
                        A: {f.a}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={() => { setSupportModal(null); setKbSearch(""); }}
            sx={{ bgcolor: tPrimary, color: "#fff", textTransform: "none", fontWeight: 700, borderRadius: "8px", boxShadow: "none", "&:hover": { bgcolor: tPrimaryDark, boxShadow: "none" } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Fixed Top Nav (demo landing spec: white, blur, hairline) ─────────── */}
      <Box
        component="header"
        sx={{
          bgcolor: "rgba(255,255,255,.86)",
          backdropFilter: "blur(14px)",
          position: "fixed",
          top: 0,
          width: "100%",
          zIndex: 1100,
          borderBottom: "1px solid var(--color-hairline-cloud)",
        }}
      >
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          maxWidth: 1180, mx: "auto", px: { xs: 2, md: 5 }, height: NAV_HEIGHT,
        }}>
          {/* Left: logo + nav links */}
          <Stack direction="row" alignItems="center" spacing={4}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              {domainTenant?.logo
                ? <Box component="img" src={domainTenant.logo} alt="" sx={{ height: 30, maxWidth: 120, objectFit: "contain", borderRadius: "8px" }} />
                : (
                  <Box sx={{ width: 30, height: 30, borderRadius: "8px", bgcolor: "var(--color-ink)", display: "grid", placeItems: "center" }}>
                    <SchoolIcon sx={{ color: "#fff", fontSize: 17 }} />
                  </Box>
                )
              }
              <Typography fontWeight={700} sx={{ fontSize: "16px", letterSpacing: "-0.01em", color: "var(--color-ink)", fontFamily: "var(--font-display)" }}>
                {tenantName || "Training Center"}
              </Typography>
            </Stack>
          </Stack>

          {/* Right: tenant selector + sign in */}
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Tenant selector — mock mode only */}
            {IS_MOCK && (
              <Box sx={{ display: { xs: "none", lg: "block" } }}>
                <Select
                  value={classSource}
                  onChange={(e) => handleSourceChange(e.target.value)}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    borderRadius: "8px",
                    minWidth: 160,
                    fontSize: "0.85rem",
                    "& .MuiSelect-icon": { color: "rgba(255,255,255,0.7)" },
                    "& fieldset": { border: "1px solid rgba(255,255,255,0.2)" },
                    "&:hover fieldset": { border: "1px solid rgba(255,255,255,0.4)" },
                    "& .MuiSelect-select": { py: "6px" },
                  }}
                >
                  <MenuItem value="public">All Centers</MenuItem>
                  {allTenants.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
                  ))}
                </Select>
              </Box>
            )}
            <Button
              onClick={() => navigate("/login")}
              sx={{
                bgcolor: "var(--color-primary)", color: "#fff",
                fontWeight: 700, fontSize: "13.5px",
                letterSpacing: "0.03em", textTransform: "uppercase",
                borderRadius: "12px", px: 3, height: 46,
                boxShadow: "none",
                "&:hover": { bgcolor: "#2a1f47", boxShadow: "none" },
                transition: "background-color 0.2s",
              }}
            >
              Sign In
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* ── Page body (padded for fixed nav) ────────────────────────────────── */}
      <Box component="main" sx={{ flex: 1, pt: `${NAV_HEIGHT}px`, pb: { xs: "72px", md: 0 } }}>

        {/* ── Hero (demo landing spec: light canvas, lime keyword chip) ─────── */}
        <Box sx={{
          bgcolor: "#fff",
          pt: { xs: 6, md: 9 }, pb: { xs: 6, md: 8 }, px: 2,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}>
          <Box sx={{ position: "relative", zIndex: 1, maxWidth: 1180, mx: "auto" }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, mb: 2.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "var(--color-accent-violet-deep)" }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--color-ink-3)" }}>
                Built for fire &amp; EMS training divisions
              </Typography>
            </Box>
            <Typography
              variant="h1"
              fontWeight={700}
              sx={{ fontSize: { xs: "2.2rem", md: "3.4rem" }, letterSpacing: "-0.02em", lineHeight: 1.1, mb: 2.5, fontFamily: "var(--font-display)", color: "var(--color-ink)" }}
            >
              Find your next{" "}
              <Box component="span" sx={{ bgcolor: "var(--color-lime)", borderRadius: "8px", px: "14px", boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" }}>
                course
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{ maxWidth: 580, mx: "auto", mb: 5, lineHeight: 1.65, fontSize: "17px", color: "var(--color-ink-2)" }}
            >
              Browse our upcoming courses and training sessions. Sign in to register
              and track your professional certifications and vocational progress.
            </Typography>

            {/* Large search bar */}
            <Box sx={{ maxWidth: 800, mx: "auto" }}>
              <Paper sx={{
                display: "flex", alignItems: "center",
                borderRadius: "12px", p: "6px",
                border: "1px solid var(--color-hairline-cloud)",
                boxShadow: "0 24px 48px -20px rgba(21,15,35,.2)",
                transition: "box-shadow 0.2s",
                "&:focus-within": { boxShadow: "0 24px 48px -20px rgba(21,15,35,.2), 0 0 0 2px var(--color-ring-focus)" },
              }}>
                <SearchIcon sx={{ color: DS.outline, ml: 1.5, mr: 0.5, flexShrink: 0, fontSize: 22 }} />
                <TextField
                  fullWidth
                  placeholder="Search by course name, code, or category..."
                  value={filter}
                  onChange={(e) => handleFilterChange(e.target.value)}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{ "& input": { py: 1.5, px: 1, fontSize: "1rem", color: DS.onSurface } }}
                />
                <Button
                  variant="contained"
                  onClick={() => {}}
                  sx={{
                    bgcolor: tPrimary, color: "#fff", fontWeight: 700,
                    borderRadius: "8px", px: { xs: 2, sm: 4 }, py: 1.75,
                    flexShrink: 0, boxShadow: "none", fontSize: "1rem",
                    "&:hover": { bgcolor: tPrimaryDark, boxShadow: "none" },
                    textTransform: "none",
                  }}
                >
                  Find Courses
                </Button>
              </Paper>
            </Box>
          </Box>
        </Box>

        {/* ── Filter Pills ──────────────────────────────────────────────────── */}
        <Box sx={{
          bgcolor: DS.surfaceContainerLow,
          borderBottom: `1px solid ${DS.outlineVariant}50`,
          position: "sticky", top: NAV_HEIGHT, zIndex: 40,
        }}>
          <Box sx={{
            maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, py: 2.5,
            display: "flex", alignItems: "center", gap: 1.25,
            overflowX: "auto", scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}>
            {["", ...CATEGORIES].map((cat) => {
              const isAll = cat === "";
              const active = isAll ? categoryFilter === "" : categoryFilter === cat;
              return (
                <Chip
                  key={cat || "__all__"}
                  label={isAll ? "All Categories" : cat}
                  onClick={() => handleCategoryChange(isAll ? "" : (cat === categoryFilter ? "" : cat))}
                  sx={{
                    flexShrink: 0,
                    fontWeight: 700, fontSize: "0.68rem", letterSpacing: "0.05em",
                    textTransform: "uppercase", borderRadius: "9999px",
                    px: 0.5, height: 32,
                    bgcolor: active ? (isAll ? tPrimary : (CATEGORY_COLORS[cat] || tPrimary)) : "#fff",
                    color: active ? "#fff" : tPrimary,
                    border: active ? "none" : `1px solid ${DS.outlineVariant}`,
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: active
                        ? (isAll ? tPrimaryDark : (CATEGORY_COLORS[cat] || tPrimaryDark))
                        : DS.surfaceContainer,
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* ── Results Grid ──────────────────────────────────────────────────── */}
        <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, py: 6 }}>
          {/* Header row */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
            <Typography variant="h5" fontWeight={700} color={DS.onSurface} sx={{ fontSize: { xs: "1.1rem", md: "1.35rem" }, fontFamily: "var(--font-display)" }}>
              {loading
                ? "Loading courses…"
                : `${filtered.length} course${filtered.length !== 1 ? "s" : ""} found`}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.06em", color: DS.onSurfaceVariant, textTransform: "uppercase" }}>
                Sort by:
              </Typography>
              <Select
                value="date"
                size="small"
                sx={{
                  fontWeight: 700, color: tPrimary, fontSize: "0.85rem",
                  "& fieldset": { border: "none" },
                  "& .MuiSelect-select": { py: 0 },
                  "& .MuiSelect-icon": { color: tPrimary },
                }}
              >
                <MenuItem value="date">Upcoming Date</MenuItem>
                <MenuItem value="title">Course Title</MenuItem>
                <MenuItem value="seats">Availability</MenuItem>
              </Select>
            </Stack>
          </Stack>

          {/* Grid */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress sx={{ color: tPrimary }} />
            </Box>
          ) : paginated.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <SchoolIcon sx={{ fontSize: 56, color: DS.outlineVariant, mb: 2 }} />
              <Typography variant="h6" color={DS.onSurfaceVariant} gutterBottom sx={{ fontFamily: "var(--font-display)" }}>No courses found</Typography>
              <Typography variant="body2" color={DS.onSurfaceVariant}>
                {filter || categoryFilter
                  ? "Try adjusting your search or filters."
                  : "Check back soon for upcoming training opportunities."}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {paginated.map((cls) => (
                <Grid item xs={12} sm={6} md={4} key={cls.id}>
                  <CourseCard cls={cls} onSelect={() => handleSelectClass(cls)} />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                sx={{
                  "& .MuiPaginationItem-root.Mui-selected": {
                    bgcolor: tPrimary, color: "#fff",
                  },
                }}
              />
            </Box>
          )}

          <PublicCenterInfo tenantId={selectedTenantForView} />

          {/* ── CTA Section ───────────────────────────────────────────────── */}
          <Box sx={{
            mt: 8, borderRadius: "24px",
            bgcolor: tPrimaryDark,
            p: { xs: 4, md: 6 },
            textAlign: "center",
            position: "relative", overflow: "hidden",
          }}>
            {/* Decorative blobs */}
            <Box sx={{
              position: "absolute", top: -60, right: -60,
              width: 200, height: 200, borderRadius: "50%",
              bgcolor: "rgba(163,60,52,0.15)", filter: "blur(48px)",
              pointerEvents: "none",
            }} />
            <Box sx={{
              position: "absolute", bottom: -60, left: -60,
              width: 200, height: 200, borderRadius: "50%",
              bgcolor: "rgba(188,236,224,0.1)", filter: "blur(48px)",
              pointerEvents: "none",
            }} />
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography variant="h4" fontWeight={700} color="#fff" sx={{ mb: 2, fontSize: { xs: "1.5rem", md: "2rem" }, letterSpacing: "-0.02em", fontFamily: "var(--font-display)" }}>
                Ready to get started?
              </Typography>
              <Typography variant="body1" color="rgba(255,255,255,0.72)" sx={{ maxWidth: 500, mx: "auto", mb: 4, lineHeight: 1.6 }}>
                Sign in to register for courses, track your certifications, and manage your comprehensive training record in one place.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/login")}
                sx={{
                  bgcolor: tSecondary, color: "#fff",
                  fontWeight: 700, fontSize: "1.05rem",
                  textTransform: "none",
                  borderRadius: "12px", px: 5, py: 1.75,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                  "&:hover": { bgcolor: tSecondaryLight, boxShadow: "0 6px 28px rgba(0,0,0,0.3)" },
                  "&:active": { transform: "scale(0.97)" },
                  transition: "all 0.2s",
                }}
              >
                Sign In to Training Center
              </Button>
            </Box>
          </Box>
        </Box>

        {/* ── Footer ────────────────────────────────────────────────────────── */}
        <Box
          component="footer"
          sx={{
            bgcolor: DS.surfaceContainerLowest,
            borderTop: `1px solid ${DS.outlineVariant}`,
            mt: "auto",
          }}
        >
          <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, py: 6 }}>
            <Grid container spacing={4} sx={{ mb: 5 }}>
              {/* Brand */}
              <Grid item xs={12} md={8}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  {domainTenant?.logo
                    ? <Box component="img" src={domainTenant.logo} alt="" sx={{ height: 22, maxWidth: 100, objectFit: "contain" }} />
                    : <SchoolIcon sx={{ color: tPrimary, fontSize: 22 }} />
                  }
                  <Typography variant="subtitle1" fontWeight={700} color={tPrimary}>
                    {tenantName || "Training Center"}
                  </Typography>
                </Stack>
                <Typography variant="body2" color={DS.onSurfaceVariant} sx={{ maxWidth: 380, lineHeight: 1.7 }}>
                  Providing industry-leading vocational training and certification tracking for professional services.
                </Typography>
              </Grid>

              {/* Support */}
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: DS.onSurface, mb: 2 }}>
                  Support
                </Typography>
                <Stack spacing={1.5}>
                  {[
                    { label: "Contact Us", key: "contact" },
                    { label: "Knowledge Base", key: "kb" },
                    { label: "System Status", key: "status" },
                  ].map(({ label, key }) => (
                    <Typography
                      key={key}
                      onClick={() => setSupportModal(key)}
                      variant="body2"
                      color={DS.onSurfaceVariant}
                      sx={{ cursor: "pointer", "&:hover": { color: tPrimary }, textDecoration: "underline", transition: "color 0.15s" }}
                    >
                      {label}
                    </Typography>
                  ))}
                </Stack>
              </Grid>
            </Grid>

            {/* Bottom bar */}
            <Divider sx={{ borderColor: DS.outlineVariant, mb: 3 }} />
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="body2" color={DS.onSurfaceVariant}>
                © {new Date().getFullYear()} Training Center. All rights reserved.
              </Typography>
              <Stack direction="row" spacing={3}>
                {[
                  { label: "Privacy Policy", key: "privacy" },
                  { label: "Terms of Service", key: "terms" },
                  { label: "Accessibility", key: "accessibility" },
                ].map(({ label, key }) => (
                  <Typography
                    key={key}
                    onClick={() => setLegalModal(key)}
                    sx={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: DS.onSurfaceVariant, cursor: "pointer", textDecoration: "underline", "&:hover": { color: tPrimary }, transition: "color 0.15s" }}
                  >
                    {label}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Box>

    </Box>
  );
}
